import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { 
  Printer, Download, Mail, Phone, Globe, MapPin,
  Calendar, Hash, User, Building2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuotePrintView = ({ quote, onClose, getAuthHeaders }) => {
  const [companyProfile, setCompanyProfile] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [contact, setContact] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchQuoteData();
  }, [quote]);

  const fetchQuoteData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchCompanyProfile(),
        fetchCustomer(),
        fetchContact()
      ]);
    } catch (error) {
      console.error('Failed to fetch quote data:', error);
      toast.error('Failed to load quote data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompanyProfile = async () => {
    try {
      const response = await axios.get(`${API}/company/profile`, getAuthHeaders());
      setCompanyProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch company profile:', error);
    }
  };

  const fetchCustomer = async () => {
    try {
      const response = await axios.get(`${API}/customers/${quote.customer_id}`, getAuthHeaders());
      setCustomer(response.data);
    } catch (error) {
      console.error('Failed to fetch customer:', error);
    }
  };

  const fetchContact = async () => {
    if (quote.contact_id) {
      try {
        // This endpoint might need to be implemented
        // const response = await axios.get(`${API}/contacts/${quote.contact_id}`, getAuthHeaders());
        // setContact(response.data);
      } catch (error) {
        console.error('Failed to fetch contact:', error);
      }
    }
  };

  const handlePrint = () => {
    // Create a new window for clean printing
    const printWindow = window.open('', '_blank', 'width=800,height=600,toolbar=no,menubar=no,location=no,status=no');
    
    const printContent = document.querySelector('.print-container').innerHTML;
    const printStyles = `
      <style>
        @page {
          size: A4;
          margin: 0.5in;
          
          /* Attempt to hide headers and footers */
          @top-left { content: ""; }
          @top-center { content: ""; }
          @top-right { content: ""; }
          @bottom-left { content: ""; }
          @bottom-center { content: ""; }
          @bottom-right { content: ""; }
        }
        
        * {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
          box-sizing: border-box;
        }
        
        html, body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          margin: 0 !important;
          padding: 0 !important;
          background: white !important;
          color: #333;
          line-height: 1.4;
          height: 100%;
          overflow: hidden;
        }
        
        .print-container {
          max-width: none !important;
          margin: 0 !important;
          padding: 0 !important;
          box-shadow: none !important;
          background: white !important;
          width: 100% !important;
          height: 100% !important;
        }
        
        /* Hide all browser elements */
        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
          }
          
          /* Aggressively remove headers/footers */
          @page {
            margin: 0.5in;
            @top-left { content: ""; }
            @top-center { content: ""; }
            @top-right { content: ""; }
            @bottom-left { content: ""; }
            @bottom-center { content: ""; }
            @bottom-right { content: ""; }
          }
          
          @page :first {
            margin-top: 0;
            @top-left { content: ""; }
            @top-center { content: ""; }
            @top-right { content: ""; }
            @bottom-left { content: ""; }
            @bottom-center { content: ""; }
            @bottom-right { content: ""; }
          }
          
          /* Hide potential footer elements */
          .chrome-print-footer,
          .print-footer,
          [data-testid="print-footer"] {
            display: none !important;
            visibility: hidden !important;
          }
        }
        
        /* Table styles */
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 10px;
        }
        
        th, td {
          border: 1px solid #666;
          padding: 4px 6px;
          text-align: left;
          vertical-align: top;
        }
        
        th {
          background-color: #14b8a6 !important;
          color: white !important;
          font-weight: 700;
          font-size: 11px;
        }
        
        /* Typography adjustments for print */
        h1 { font-size: 20px; margin: 0 0 8px 0; }
        h2 { font-size: 24px; margin: 0 0 12px 0; }
        h3 { font-size: 14px; margin: 0 0 8px 0; }
        h4 { font-size: 13px; margin: 0 0 4px 0; }
        
        .compact-text {
          font-size: 10px;
          line-height: 1.3;
        }
        
        .compact-header {
          font-size: 13px;
        }
        
        /* Spacing adjustments */
        .print-header { margin-bottom: 4mm; }
        .print-customer { margin-bottom: 3mm; }
        .print-items { margin-bottom: 2mm; }
        .print-totals { margin-bottom: 2mm; }
        .print-notes { margin-bottom: 2mm; }
        .print-terms { margin-bottom: 2mm; }
        
        /* Color preservation */
        .bg-gray-50 { background-color: #f9fafb !important; }
        .bg-gray-100 { background-color: #f3f4f6 !important; }
        .bg-blue-50 { background-color: #eff6ff !important; }
        .text-blue-600 { color: #2563eb !important; }
        .text-blue-800 { color: #1e40af !important; }
        .border-gray-300 { border-color: #d1d5db !important; }
        
        /* Flexbox and grid adjustments */
        .flex { display: flex; }
        .justify-between { justify-content: space-between; }
        .justify-end { justify-content: flex-end; }
        .items-start { align-items: flex-start; }
        .items-center { align-items: center; }
        .gap-2 { gap: 8px; }
        .gap-3 { gap: 12px; }
        
        /* Spacing utilities */
        .mb-1 { margin-bottom: 4px; }
        .mb-2 { margin-bottom: 8px; }
        .mb-4 { margin-bottom: 16px; }
        .mb-8 { margin-bottom: 32px; }
        .mt-0\.5 { margin-top: 2px; }
        .p-3 { padding: 12px; }
        .p-4 { padding: 16px; }
        .px-2 { padding-left: 8px; padding-right: 8px; }
        .px-3 { padding-left: 12px; padding-right: 12px; }
        .py-1 { padding-top: 4px; padding-bottom: 4px; }
        .py-2 { padding-top: 8px; padding-bottom: 8px; }
        .py-3 { padding-top: 12px; padding-bottom: 12px; }
        
        /* Text utilities */
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .text-sm { font-size: 12px; }
        .text-xs { font-size: 10px; }
        .font-medium { font-weight: 500; }
        .font-semibold { font-weight: 600; }
        .font-bold { font-weight: 700; }
        
        /* Border utilities */
        .border { border: 1px solid #d1d5db; }
        .border-b { border-bottom: 1px solid #d1d5db; }
        .border-t-2 { border-top: 2px solid #d1d5db; }
        .rounded { border-radius: 4px; }
        .rounded-lg { border-radius: 8px; }
        
        /* Width utilities */
        .w-4 { width: 16px; }
        .w-5 { width: 20px; }
        .w-16 { width: 64px; }
        .w-80 { width: 320px; }
        .h-16 { height: 64px; }
        
        /* Icon sizing */
        svg {
          width: 16px;
          height: 16px;
          display: inline-block;
          vertical-align: middle;
        }
      </style>
    `;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Quote-${quote.quote_number}</title>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          ${printStyles}
        </head>
        <body>
          <div class="print-container">
            ${printContent}
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                };
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  const handleEmail = async () => {
    try {
      await axios.post(`${API}/quotes/${quote.id}/send`, {}, getAuthHeaders());
      toast.success('Quote emailed successfully');
    } catch (error) {
      toast.error('Failed to email quote');
    }
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatAddress = (profile) => {
    if (!profile) return '';
    const parts = [
      profile.address_line1,
      profile.address_line2,
      profile.address_line3,
      profile.city,
      profile.county,
      profile.postcode
    ].filter(Boolean);
    return parts.join(', ');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading quote...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full bg-white">
      {/* Enhanced Print Styles */}
      <style jsx>{`
        @page {
          size: A4;
          margin: 0.5in;
        }
        
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          /* Hide everything that's not the quote */
          .no-print { 
            display: none !important; 
          }
          
          /* Reset browser defaults */
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
            background: white !important;
          }
          
          /* Clean container for print */
          .print-container { 
            max-width: none !important; 
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            background: white !important;
            width: 100% !important;
            height: auto !important;
            min-height: auto !important;
            page-break-inside: avoid;
            position: relative;
          }
          
          /* Compact spacing for print */
          .print-header { margin-bottom: 4mm !important; }
          .print-customer { margin-bottom: 3mm !important; }
          .print-items { margin-bottom: 2mm !important; }
          .print-totals { margin-bottom: 2mm !important; }
          .print-notes { margin-bottom: 2mm !important; }
          .print-terms { margin-bottom: 2mm !important; }
          
          /* Enhanced Table styling */
          table {
            width: 100% !important;
            border-collapse: collapse !important;
            font-size: 12px !important;
          }
          
          th, td {
            padding: 4px 6px !important;
            border: 1px solid #666 !important;
            vertical-align: top !important;
          }
          
          th {
            background-color: #14b8a6 !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 11px !important;
          }
          
          tbody td {
            font-size: 10px !important;
            line-height: 1.3 !important;
          }
          
          /* Body text improvements */
          p, div, span {
            font-size: 12px !important;
            line-height: 1.5 !important;
          }
          
          .text-sm {
            font-size: 12px !important;
          }
          
          .text-xs {
            font-size: 11px !important;
          }
          
          /* Contact info styling */
          .contact-info {
            font-size: 11px !important;
            line-height: 1.4 !important;
          }
          
          /* Enhanced Typography for print */
          .compact-text {
            font-size: 11px !important;
            line-height: 1.4 !important;
          }
          
          .compact-header {
            font-size: 16px !important;
            font-weight: 600 !important;
          }
          
          h1 { 
            font-size: 22px !important; 
            margin-bottom: 10px !important; 
            font-weight: 700 !important;
          }
          h2 { 
            font-size: 28px !important; 
            margin-bottom: 12px !important; 
            font-weight: 700 !important;
          }
          h3 { 
            font-size: 16px !important; 
            margin-bottom: 8px !important; 
            font-weight: 600 !important;
          }
          h4 { 
            font-size: 14px !important; 
            margin-bottom: 6px !important; 
            font-weight: 600 !important;
          }
          
          /* Force visibility of content */
          .bg-gray-50, .bg-gray-100, .bg-blue-50 {
            background-color: #f5f5f5 !important;
            -webkit-print-color-adjust: exact !important;
          }
        }
        
        /* Screen styles */
        .print-container {
          background: white;
          max-width: 210mm;
          margin: 0 auto;
          padding: 15mm;
          box-shadow: 0 0 10px rgba(0,0,0,0.1);
          min-height: calc(297mm - 30mm);
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }
      `}</style>

      {/* Action Buttons - Hidden in print */}
      <div className="no-print flex justify-between items-start mb-6 p-4 bg-gray-50 rounded-lg">
        <div>
          <h2 className="text-xl font-bold mb-2">Quote Preview</h2>
          <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded border-l-4 border-blue-400">
            <p className="font-medium mb-1">💡 For cleanest printing:</p>
            <p>In the print dialog, click "More settings" → uncheck "Headers and footers" to remove URLs and page numbers.</p>
          </div>
        </div>
        <div className="flex gap-2 ml-4">
          <Button onClick={handleEmail} className="gap-2">
            <Mail className="w-4 h-4" />
            Email Quote
          </Button>
          <Button onClick={handlePrint} variant="outline" className="gap-2">
            <Printer className="w-4 h-4" />
            Print (Clean)
          </Button>
          <Button onClick={() => window.print()} variant="outline" className="gap-2">
            <Printer className="w-4 h-4" />
            Quick Print
          </Button>
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>

      {/* Quote Document */}
      <div className="print-container">
        {/* Header - Company Logo and QUOTATION Title */}
        <div className="flex justify-between items-center mb-6 print-header">
          {/* Company Logo */}
          <div className="flex-1">
            {companyProfile?.logo_url ? (
              <img 
                src={companyProfile.logo_url} 
                alt="Company Logo" 
                className="h-16 object-contain"
              />
            ) : (
              <div className="h-12 w-32 bg-teal-600 text-white flex items-center justify-center text-lg font-bold rounded">
                {companyProfile?.company_name?.split(' ').map(word => word.charAt(0)).join('') || 'SAMPLE LOGO'}
              </div>
            )}
          </div>

          {/* QUOTATION Title and Details */}
          <div className="text-right">
            <h1 className="text-3xl font-bold text-gray-700 mb-3">QUOTATION</h1>
            
            <div className="space-y-1 text-xs text-gray-600">
              <div className="flex justify-between min-w-44">
                <span className="font-medium">Quote No.:</span>
                <span className="font-bold">{quote.quote_number}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium">Quotation Date:</span>
                <span>{formatDate(quote.created_at)}</span>
              </div>
              
              {quote.valid_until && (
                <div className="flex justify-between">
                  <span className="font-medium">Valid Until:</span>
                  <span>{formatDate(quote.valid_until)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* BILL TO Section */}
        <div className="mb-6 print-customer">
          <div className="bg-teal-600 text-white px-3 py-2 text-sm font-bold">
            BILL TO
          </div>
          <div className="border border-teal-600 border-t-0 p-3 bg-white">
            <div className="text-sm text-gray-700">
              <div className="font-bold text-gray-900 mb-1">{customer?.name || 'Customer Name'}</div>
              {customer?.name && <div>{customer.name}</div>}
              {customer?.address_1 && <div>{customer.address_1}</div>}
              {customer?.city && (
                <div>
                  {[customer.city, customer.county, customer.postcode]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              )}
              {customer?.email && <div>{customer.email}</div>}
              {customer?.phone && <div>{customer.phone}</div>}
            </div>
          </div>
        </div>

        {/* Services Table */}
        <div className="mb-4 print-items">
          <table className="w-full border-collapse border border-gray-400">
            <thead>
              <tr className="bg-teal-600 text-white">
                <th className="border border-gray-400 px-2 py-2 text-left text-xs font-bold w-8">#</th>
                <th className="border border-gray-400 px-2 py-2 text-left text-xs font-bold">Item Description</th>
                <th className="border border-gray-400 px-2 py-2 text-center text-xs font-bold w-16">Hours</th>
                <th className="border border-gray-400 px-2 py-2 text-right text-xs font-bold w-20">Rate/Hour</th>
                <th className="border border-gray-400 px-2 py-2 text-right text-xs font-bold w-24">Line Total</th>
              </tr>
            </thead>
            <tbody>
              {quote.items.map((item, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="border border-gray-400 px-2 py-2 text-xs text-center">
                    {index + 1}
                  </td>
                  <td className="border border-gray-400 px-2 py-2 text-xs">
                    <div className="font-medium">{item.description}</div>
                  </td>
                  <td className="border border-gray-400 px-2 py-2 text-center text-xs">
                    {item.quantity}
                  </td>
                  <td className="border border-gray-400 px-2 py-2 text-right text-xs">
                    {formatCurrency(item.unit_price)}
                  </td>
                  <td className="border border-gray-400 px-2 py-2 text-right text-xs font-medium">
                    {formatCurrency(item.line_total)}
                  </td>
                </tr>
              ))}
              
              {/* Add empty rows to ensure space for 5 items total */}
              {Array.from({ length: Math.max(0, 5 - quote.items.length) }, (_, i) => (
                <tr key={`empty-${i}`} className={(quote.items.length + i) % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="border border-gray-400 px-2 py-2 text-xs text-center">
                    {quote.items.length + i + 1}
                  </td>
                  <td className="border border-gray-400 px-2 py-2 text-xs">&nbsp;</td>
                  <td className="border border-gray-400 px-2 py-2 text-xs">&nbsp;</td>
                  <td className="border border-gray-400 px-2 py-2 text-xs">&nbsp;</td>
                  <td className="border border-gray-400 px-2 py-2 text-xs">&nbsp;</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Bottom Section - Payment Info and Totals */}
        <div className="flex justify-between mb-4">
          {/* Payment Information */}
          <div className="w-2/5">
            <h4 className="text-sm font-bold text-gray-700 mb-2">Payment Information</h4>
            <div className="text-xs text-gray-600 space-y-1">
              <div><span className="font-medium">Bank Name:</span> {companyProfile?.bank_name || 'Bank Name'}</div>
              <div><span className="font-medium">Account Name:</span> {companyProfile?.account_name || companyProfile?.company_name || 'Account Name'}</div>
              <div><span className="font-medium">Sort Code:</span> {companyProfile?.sort_code || 'XX-XX-XX'}</div>
              <div><span className="font-medium">Account Number:</span> {companyProfile?.account_number || '12345567-22'}</div>
            </div>
          </div>

          {/* Totals Section */}
          <div className="w-1/3">
            <table className="w-full border-collapse border border-gray-400 text-xs">
              <tbody>
                <tr>
                  <td className="border border-gray-400 px-2 py-1 font-medium text-right">Sub Total:</td>
                  <td className="border border-gray-400 px-2 py-1 text-right font-bold">{formatCurrency(quote.subtotal)}</td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-2 py-1 font-medium text-right">Tax:</td>
                  <td className="border border-gray-400 px-2 py-1 text-right font-bold">{formatCurrency(quote.tax_total)}</td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-2 py-1 font-medium text-right">Discount:</td>
                  <td className="border border-gray-400 px-2 py-1 text-right font-bold">{formatCurrency(0)}</td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-2 py-1 font-medium text-right">Others:</td>
                  <td className="border border-gray-400 px-2 py-1 text-right font-bold">{formatCurrency(0)}</td>
                </tr>
                <tr className="bg-teal-600 text-white">
                  <td className="border border-gray-400 px-2 py-2 font-bold text-right">Total:</td>
                  <td className="border border-gray-400 px-2 py-2 text-right font-bold text-lg">{formatCurrency(quote.total)}</td>
                </tr>
              </tbody>
            </table>
            
            {quote.deposit_required && (
              <div className="mt-2 bg-yellow-50 border border-yellow-200 p-2 rounded">
                <div className="text-xs font-medium text-yellow-800">
                  Deposit Required: {formatCurrency(quote.deposit_amount)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Terms Section */}
        {quote.notes_public && (
          <div className="mb-4 print-notes">
            <h4 className="text-sm font-bold text-gray-700 mb-2">Additional Information</h4>
            <p className="text-xs text-gray-600 leading-relaxed">{quote.notes_public}</p>
          </div>
        )}

        {/* Terms and Conditions */}
        <div className="mb-4 print-terms">
          <h4 className="text-sm font-bold text-gray-700 mb-1">Terms and Conditions</h4>
          <div className="text-xs text-gray-600 leading-relaxed">
            This quote is valid for {quote.valid_until ? `until ${formatDate(quote.valid_until)}` : '30 days from the date above'}. 
            Prices include VAT where applicable. Payment terms: {quote.deposit_required ? `${formatCurrency(quote.deposit_amount)} deposit required, balance on completion` : 'Payment due on completion'}. 
            We reserve the right to amend prices due to unforeseen circumstances. Customer must provide reasonable access to the property and vehicles.
          </div>
        </div>

        {/* Thank you message */}
        <div className="text-center mb-4">
          <p className="text-teal-600 text-sm font-medium">Thank you for your Business!</p>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-300 pt-2 text-center">
          <p className="text-xs text-gray-500 uppercase font-medium">
            {companyProfile?.company_name || 'COMPANY NAME HERE'} | {formatAddress(companyProfile) || 'address, city state, Zip Code'} | 
            {companyProfile?.email || 'email@OfficeTemplatesOnline.com'} | {companyProfile?.phone || '123-456-7890'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuotePrintView;